import React, { useCallback, useRef, useState } from 'react';
import { useUniqueId } from '../utils/useUniqueId';
import { Button } from '../button/Button';
import { LinkButton } from '../button';
import { Input } from '../form/input/Input';
import { FieldLabel } from '../form/field-label/FieldLabel';
import { HelpText } from '../form/help-text/HelpText';
import { DatePickerField, formatLocalDateTime, parseLocalDateTime } from '../historyPicker/DatePickerField';
import { Dropdown } from '../dropdown/Dropdown';
import { EditSlideIn } from '../slidein/EditSlideIn';
import { ANIMATION_DURATION_IN_MILLIS } from '../slidein/SlideIn.styled';
import { DropdownToggle } from '../dropdown-toggle/DropdownToggle';
import { SimpleToast } from '../simple-toast/SimpleToast';
import type { DataSourceField, AdapterContext, SelectOption } from './types';
import {
	Wrapper,
	Header,
	HeaderLeft,
	HeaderRight,
	PlusIcon,
	TitleBlock,
	Title,
	Subtitle,
	FormFieldWrapper,
	LabelRow
} from './ManualDataRegistration.styled';

export type ManualDataRegistrationToast = {
	headline: string;
	text: string;
	variant?: 'success' | 'info' | 'warning' | 'danger';
};

export type ManualDataRegistrationProps = {
	uuid: string;
	title: string;
	subtitle?: string;
	fields: DataSourceField[];
	context?: AdapterContext;
	openInNewTabLabel?: string;
	addNewRegistrationLabel?: string;
	onSave?: (values: Record<string, string>) => void | Promise<void>;
	saveLabel?: string;
	cancelLabel?: string;
	className?: string;
	toast?: ManualDataRegistrationToast;
	errorToast?: ManualDataRegistrationToast;
	initialValues?: Record<string, string>;
};

// ---------------------------------------------------------------------------
// Internal field rendering — derived directly from API field types
// ---------------------------------------------------------------------------

function parseChoiceOptions(defaultValue: string): SelectOption[] {
	if (!defaultValue) return [];
	return defaultValue
		.split('\n')
		.map((line) => line.replace(/\r/g, '').trim())
		.filter((line) => line !== '')
		.map((line) => ({ value: line, label: line }));
}

type FieldRenderProps = {
	id: string;
	value: string;
	hasError: boolean;
	onChange: (value: string) => void;
	onDropdownOpen?: () => void;
	dropdownKey?: string;
};

function renderField(field: DataSourceField, context: AdapterContext, props: FieldRenderProps) {
	switch (field.input) {
	case 'TextInput':
		return (
			<Input
				id={props.id}
				type="text"
				value={props.value}
				hasError={props.hasError}
				onChange={(e) => props.onChange(e.target.value)}
			/>
		);
	case 'DecimalInput':
		return (
			<Input
				id={props.id}
				type="number"
				value={props.value}
				hasError={props.hasError}
				onChange={(e) => props.onChange(e.target.value)}
			/>
		);
	case 'DateInput':
		return (
			<DatePickerField
				id={props.id}
				value={parseLocalDateTime(props.value)}
				hasError={props.hasError}
				onChange={(date) => props.onChange(formatLocalDateTime(date))}
				includeTime
				showIcon
			/>
		);
	case 'BooleanInput':
		return (
			<Dropdown
				key={props.dropdownKey}
				value={props.value || undefined}
				options={[
					{ value: 'true', label: 'Yes' },
					{ value: 'false', label: 'No' },
				]}
				hasError={props.hasError}
				searchable
				tabbable
				onOpen={props.onDropdownOpen}
				onSelect={(value) => props.onChange(String(value))}
			/>
		);
	case 'ChoiceInput':
	case 'ChoiceWithoutDefaultInput':
		return (
			<Dropdown
				key={props.dropdownKey}
				value={props.value || undefined}
				options={parseChoiceOptions(field.default_value)}
				hasError={props.hasError}
				searchable
				tabbable
				onOpen={props.onDropdownOpen}
				onSelect={(value) => props.onChange(String(value))}
			/>
		);
	case 'CountryInput':
		return (
			<Dropdown
				key={props.dropdownKey}
				value={props.value || undefined}
				options={context.countryOptions ?? []}
				hasError={props.hasError}
				searchable
				tabbable
				onOpen={props.onDropdownOpen}
				onSelect={(value) => props.onChange(String(value))}
			/>
		);
	case 'MemberInput':
		return (
			<Dropdown
				key={props.dropdownKey}
				value={props.value || undefined}
				options={context.memberOptions ?? []}
				hasError={props.hasError}
				searchable
				tabbable
				onOpen={props.onDropdownOpen}
				onSelect={(value) => props.onChange(String(value))}
			/>
		);
	case 'RelationInput': {
		const options = field.related_data_source_uuid
			? context.relationOptionsMap?.[field.related_data_source_uuid] ?? []
			: [];
		return (
			<Dropdown
				key={props.dropdownKey}
				value={props.value || undefined}
				options={options}
				hasError={props.hasError}
				searchable
				tabbable
				onOpen={props.onDropdownOpen}
				onSelect={(value) => props.onChange(String(value))}
			/>
		);
	}
	}
}

function buildInitialValues(
	fields: DataSourceField[],
	overrides: Record<string, string> = {}
): Record<string, string> {
	const initialValues: Record<string, string> = {};
	for (const field of fields) {
		if (field.input === 'ChoiceInput') {
			const options = parseChoiceOptions(field.default_value);
			if (options.length > 0) {
				initialValues[field.uuid] = options[0].value;
			}
		}
	}
	return { ...initialValues, ...overrides };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ManualDataRegistration = ({
	uuid,
	title,
	subtitle,
	fields,
	context = {},
	openInNewTabLabel = 'Open in new tab',
	addNewRegistrationLabel = 'Add new registration',
	onSave,
	saveLabel = 'Save',
	cancelLabel = 'Cancel',
	className,
	toast,
	errorToast,
	initialValues: initialValuesProp,
}: ManualDataRegistrationProps) => {
	const initialValues = React.useMemo(
		() => buildInitialValues(fields, initialValuesProp),
		[fields, initialValuesProp]
	);

	const [isOpen, setIsOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [values, setValues] = useState<Record<string, string>>(initialValues);
	const [errors, setErrors] = useState<Record<string, boolean>>({});
	const [showToast, setShowToast] = useState(false);
	const [showErrorToast, setShowErrorToast] = useState(false);
	const [closeGenerations, setCloseGenerations] = useState<Record<string, number>>({});
	const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const id = useUniqueId('manual-data-registration');
	const actionId = useUniqueId('manual-data-registration-action');

	const handleOpen = () => {
		if (resetTimeoutRef.current) {
			clearTimeout(resetTimeoutRef.current);
			resetTimeoutRef.current = null;
		}
		setIsOpen(true);
	};

	const handleClose = useCallback(() => {
		if (resetTimeoutRef.current) {
			clearTimeout(resetTimeoutRef.current);
		}
		setIsOpen(false);
		resetTimeoutRef.current = setTimeout(() => {
			setValues(initialValues);
			setErrors({});
			resetTimeoutRef.current = null;
		}, ANIMATION_DURATION_IN_MILLIS);
	}, [initialValues]);

	const handleFieldChange = (field: DataSourceField, value: string) => {
		setValues((prev) => ({ ...prev, [field.uuid]: value }));

		const isValid = field.input === 'BooleanInput' ? value === 'true' || value === 'false' : !!value.trim();
		if (isValid) {
			setErrors((prev) => ({ ...prev, [field.uuid]: false }));
		} else if (!field.blank) {
			setErrors((prev) => ({ ...prev, [field.uuid]: true }));
		}
	};

	const handleSave = async () => {
		if (isSaving) return;

		const newErrors: Record<string, boolean> = {};
		for (const field of fields) {
			if (field.blank) continue;
			if (field.input === 'BooleanInput') {
				const v = values[field.uuid];
				if (v !== 'true' && v !== 'false') {
					newErrors[field.uuid] = true;
				}
			} else if (!values[field.uuid]?.trim()) {
				newErrors[field.uuid] = true;
			}
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		setIsSaving(true);
		try {
			await onSave?.(values);
			handleClose();
			if (toast) {
				setShowToast(true);
			}
		} catch {
			if (errorToast) {
				setShowErrorToast(true);
			}
		} finally {
			setIsSaving(false);
		}
	};
	const dropdownActions: React.ReactElement[] = [
		<LinkButton
			key={`${actionId}-open`}
			label={openInNewTabLabel}
			icon="external-link"
			href={`/data/${uuid}/new/`}
			target="_blank"
		/>,
		<Button
			key={`${actionId}-add`}
			label={addNewRegistrationLabel}
			icon="plus-circle"
			onClick={handleOpen}
		/>,
	];

	return (
		<Wrapper className={className}>
			<Header
				tabIndex={0}
				onClick={handleOpen}
				onKeyDown={(e) => {
					if (e.target !== e.currentTarget) return;
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						handleOpen();
					}
				}}
			>
				<HeaderLeft>
					<PlusIcon icon="plus" />
					<TitleBlock>
						<Title>{title}</Title>
						{subtitle && <Subtitle>{subtitle}</Subtitle>}
					</TitleBlock>
				</HeaderLeft>

				<HeaderRight onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
					<DropdownToggle variant="ghost">
						{dropdownActions}
					</DropdownToggle>
				</HeaderRight>
			</Header>

			<EditSlideIn
				open={isOpen}
				title={title}
				onClose={handleClose}
				onSave={handleSave}
				isSaving={isSaving}
				saveText={saveLabel}
				cancelText={cancelLabel}
			>
				{fields.map((field, index) => {
					const fieldId = `manual-data-registration-field-${id}-${index}`;
					return (
						<FormFieldWrapper key={field.uuid}>
							<LabelRow>
								<FieldLabel htmlFor={fieldId} required={!field.blank}>
									{field.name}
								</FieldLabel>
							</LabelRow>
							{field.description ? <HelpText>{field.description}</HelpText> : null}

							{renderField(field, context, {
								id: fieldId,
								value: values[field.uuid] ?? '',
								hasError: !!errors[field.uuid],
								onChange: (value) => handleFieldChange(field, value),
								onDropdownOpen: () => setCloseGenerations(prev => {
									const next = { ...prev };
									for (const f of fields) {
										if (f.uuid !== field.uuid) {
											next[f.uuid] = (prev[f.uuid] ?? 0) + 1;
										}
									}
									return next;
								}),
								dropdownKey: `${field.uuid}-${closeGenerations[field.uuid] ?? 0}`,
							})}
						</FormFieldWrapper>
					);
				})}
			</EditSlideIn>

			{toast && (
				<SimpleToast
					headline={toast.headline}
					text={toast.text}
					variant={toast.variant ?? 'success'}
					isVisible={showToast}
					onHide={() => setShowToast(false)}
				/>
			)}

			{errorToast && (
				<SimpleToast
					headline={errorToast.headline}
					text={errorToast.text}
					variant={errorToast.variant ?? 'danger'}
					isVisible={showErrorToast}
					onHide={() => setShowErrorToast(false)}
				/>
			)}
		</Wrapper>
	);
};
